<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import { navigationActions } from "@/action/navigationActions";
import { objectiveActions } from "@/action/objectiveActions";
import { objectiveMarkedAs } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import ExpandableTextarea from "@/components/input/ExpandableTextarea.vue";
import MaxLengthTextarea from "@/components/input/MaxLengthTextarea.vue";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { boardTypeToLevel } from "@/model/baseTypes";
import type { BoardWithObjectives } from "@/model/board";
import type { Objective, ObjectiveType } from "@/model/objective";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";

import ObjectiveCardAttributes from "./ObjectiveCardAttributes.vue";
import SetObjectivePoints from "./SetObjectivePoints.vue";

const props = defineProps<{
  objective: Objective;
  type: ObjectiveType;
  index: number;
  board: BoardWithObjectives;
  isDragging: boolean;
  expandDescriptions: boolean;
  hasStatusDistribution: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
}>();

const emit = defineEmits<{ removed: [] }>();

const isFocused = ref(false);

watch(
  () => props.isDragging,
  () => setIsFocused(false),
);

const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);
const readonly = computed(() => !useUserStore().isAllowed("edit"));

function setTitle(title: string) {
  objectiveActions.setTitle("modal", props.board.id, props.objective, title);
}

function setDescription(description: string) {
  objectiveActions.setDescription("modal", props.board.id, props.objective, description);
}

function setIsFocused(focused: boolean) {
  isFocused.value = focused;
}

function setBusinessValue(value: number) {
  objectiveActions.setBv(
    "modal",
    props.board.id,
    props.objective,
    props.type === "committed",
    value,
  );
}

function setActualValue(value: number) {
  objectiveActions.setAv(
    "modal",
    props.board.id,
    props.objective,
    props.type === "committed",
    value,
  );
}

async function markAsUncommitted() {
  objectiveActions.move("modal", props.board.id, props.objective.id, {
    stretch: true,
    rank: props.board.stretchObjectives.length,
  });

  trackEvent(objectiveMarkedAs(props.objective.id, true, boardTypeToLevel(props.board.type)));

  // move focus to the objective after it's rerendered in the new state
  await nextTick();
  document.getElementById(`objective-card-${props.objective.id}`)?.focus();
}

async function markAsCommitted() {
  objectiveActions.move("modal", props.board.id, props.objective.id, {
    stretch: false,
    rank: props.board.objectives.length,
  });

  trackEvent(objectiveMarkedAs(props.objective.id, false, boardTypeToLevel(props.board.type)));

  // move focus to the objective after it's rerendered in the new state
  await nextTick();
  document.getElementById(`objective-card-${props.objective.id}`)?.focus();
}

function remove() {
  objectiveActions.remove("modal", props.board.id, props.objective.id);

  // Notify the parent component that the objective has been removed
  emit("removed");
}

/**
 * Move the objective up in the list
 */
function moveUp() {
  objectiveActions.move("context-menu", props.board.id, props.objective.id, {
    stretch: props.type === "uncommitted",
    rank: props.index - 2, // (local index is 1-based, API index is 0-based)
  });
}

/**
 * Move the objective down in the list
 */
function moveDown() {
  objectiveActions.move("context-menu", props.board.id, props.objective.id, {
    stretch: props.type === "uncommitted",
    rank: props.index, // (local index is 1-based, API index is 0-based)
  });
}

function showBreakdown() {
  navigationActions.showBreakdown("art-objectives-board", {
    type: "objective",
    id: props.objective.id,
    boardId: props.board.id,
  });
}
</script>

<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
<template>
  <article
    :id="`objective-card-${objective.id}`"
    class="objective-card"
    tabindex="-1"
    :data-testid="`objective-card-${objective.id}`"
    :data-id="objective.id"
    :aria-label="$t('label.objective', { text: objective.text, index })"
    @mouseenter="setIsFocused(true)"
    @mouseleave="setIsFocused(false)"
  >
    <!-- eslint-enable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
    <div
      class="objective-inner-container"
      :class="{ focused: isFocused && !isDragging, dragging: isDragging }"
    >
      <div class="objective-title">
        <div v-if="isFocused && !isDragging && !readonly" class="objective-drag-icon">
          <SvgIcon name="arrow/up-down" width="20" height="20" />
        </div>
        <div v-else class="objective-index">{{ index }}</div>
        <MaxLengthTextarea
          spellcheck="false"
          :placeholder="$t('objectives.title.placeholder')"
          :model-value="objective.text"
          :maxlength="260"
          :readonly
          bordered
          @update:model-value="setTitle"
        />
        <IconButton
          icon="board/breakdown"
          :text="$t('breakdownModal.title')"
          @click="showBreakdown"
        />
        <DropdownMenu v-if="!readonly">
          <template #trigger="{ isOpen }">
            <IconButton
              data-dropdown-trigger
              icon="system/more-horizontal"
              :aria-label="$t('label.objective.menu')"
              :aria-expanded="isOpen"
              aria-controls="objective-card-dropdown-menu"
              aria-haspopup="menu"
            />
          </template>
          <MenuList id="objective-card-dropdown-menu" role="menu">
            <MenuListItems>
              <MenuListItem v-if="type === 'committed'" @click="markAsUncommitted">
                <template #before>
                  <SvgIcon name="general/uncommitted" width="20" height="20" />
                </template>
                {{ $t("objective.markAsUncommitted") }}
              </MenuListItem>
              <MenuListItem v-if="type === 'uncommitted'" @click="markAsCommitted">
                <template #before>
                  <SvgIcon name="general/committed" width="20" height="20" />
                </template>
                {{ $t("objective.markAsCommitted") }}
              </MenuListItem>
              <MenuListItem v-if="canMoveUp" @click="moveUp">
                <template #before>
                  <SvgIcon name="arrow/chevron-up" width="20" height="20" />
                </template>
                {{ $t("objective.moveUp") }}
              </MenuListItem>
              <MenuListItem v-if="canMoveDown" @click="moveDown">
                <template #before>
                  <SvgIcon name="arrow/chevron-down" width="20" height="20" />
                </template>
                {{ $t("objective.moveDown") }}
              </MenuListItem>
              <MenuListItem @click="remove">
                <template #before>
                  <SvgIcon name="general/delete" width="20" height="20" />
                </template>
                {{ $t("general.delete") }}
              </MenuListItem>
            </MenuListItems>
          </MenuList>
        </DropdownMenu>
      </div>
      <div class="objective-description">
        <ExpandableTextarea
          :expand-description="expandDescriptions"
          :model-value="objective.description"
          spellcheck="false"
          :readonly
          @update:model-value="setDescription"
        />
      </div>

      <div class="objective-details">
        <div class="objective-values">
          <div class="objective-value-title">
            <span aria-hidden="true">
              <BaseTooltip v-if="isExecutionMode">
                {{ $t("objectives.bv") }}
                <template #content>
                  {{ $t("objectives.businessValue") }}
                </template>
              </BaseTooltip>
              <div v-else>{{ $t("objectives.businessValue") }}</div>
            </span>
            <div class="screen-reader-only">
              {{ $t("label.objective.bv", { value: objective.bv }) }}
            </div>
          </div>
          <SetObjectivePoints
            :current="objective.bv"
            :readonly
            :aria-label="$t('label.objective.bvButton')"
            @select="setBusinessValue"
          />
          <div v-if="isExecutionMode" class="objective-value-title actual-value">
            <span aria-hidden="true">
              <BaseTooltip>
                {{ $t("objectives.av") }}
                <template #content>
                  {{ $t("objectives.actualValue") }}
                </template>
              </BaseTooltip>
            </span>
            <div class="screen-reader-only">
              {{ $t("label.objective.av", { value: objective.av }) }}
            </div>
          </div>
          <SetObjectivePoints
            v-if="isExecutionMode"
            :current="objective.av"
            :readonly
            :aria-label="$t('label.objective.avButton')"
            @select="setActualValue"
          />
        </div>
        <MenuItemDelimiter />
        <ObjectiveCardAttributes :objective :board :has-status-distribution />
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/utils";
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/mixins/a11y";
@use "@/styles/mixins/typography";

.objective-card {
  margin-bottom: 12px;
  background-color: colors.$background-ui;
  border-radius: variables.$objective-card-border-radius;

  .objective-title {
    display: flex;
    margin-bottom: 4px;
    align-items: center;

    base-textarea {
      width: auto;
    }

    .objective-index {
      @include typography.medium("semi-bold");

      color: colors.$text-subtle;
      margin-right: 4px;
    }

    .objective-drag-icon {
      margin: 0 0 0 -8px;
    }
  }

  .objective-inner-container {
    border: 1px solid colors.$border-subtle;
    padding: 10px 16px 16px;
    border-radius: variables.$objective-card-border-radius;

    &.focused {
      border: 1px solid colors.$border;
      cursor: pointer;
    }

    &.dragging {
      cursor: grabbing;
    }
  }

  .objective-details {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .objective-description {
    padding-left: 0.8125rem;
    margin-bottom: 0.75rem;

    :deep(.description-textarea) {
      @include typography.medium("medium");

      position: static;
      color: colors.$text;
    }

    :deep(.description-summary) {
      @include typography.medium("medium");
    }
  }

  .objective-values {
    display: flex;
    align-items: center;
    padding-left: 21px;

    .objective-value-title {
      @include typography.small("medium");

      margin-right: 8px;

      &.actual-value {
        margin-left: 20px;
      }
    }
  }

  .list-item > * {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  &.objective-drag-state > .objective-inner-container {
    visibility: hidden;
  }

  .screen-reader-only {
    @include a11y.screen-reader-only;
  }
}
</style>

<style lang="scss">
.objective-card {
  .status-distribution .bordered {
    border-width: 2px;
  }
}

.objective-menu-item {
  padding: 0;
}
</style>
